import type { Conversation } from '@atlassian/customer-service-management-widget-sdk-types/Conversation';
import type { CSMSDK } from '@atlassian/customer-service-management-widget-sdk-types/CSMSDK';

type SDKCommands = Parameters<CSMSDK>;

type CommandParams<Command extends SDKCommands[0]> =
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	Extract<SDKCommands, [Command, ...any[]]> extends [Command, ...infer Args] ? Args : never;

const createCommand = <Command extends SDKCommands[0]>(command: Command) => {
	const fn = (...args: CommandParams<Command>): void => {
		if (typeof window === 'undefined' || !window.CSM) {
			// eslint-disable-next-line no-console
			console.warn('CSM SDK has not been loaded!');
			return;
		}

		// @ts-expect-error - window.CSM is dynamically typed; TypeScript cannot infer the overloaded command signature at call site
		window.CSM(command, ...args);
	};

	return fn;
};

/**
 * Open the widget
 */
export const open: () => void = createCommand('open');

/**
 * Close the widget
 */
export const close: () => void = createCommand('close');

/**
 * Hide the widget off the screen
 */
export const hide: () => void = createCommand('hide');

/**
 * Show the widget on the screen
 */
export const show: () => void = createCommand('show');

/**
 * Update the widget style
 */
export const update: (args_0: { zIndex: number }) => void = createCommand('update');

/**
 * Create a new conversation
 */
export const createNewConversation: (args_0: { message?: string }) => void =
	createCommand('createNewConversation');

/**
 * Subscribe to the widget open event
 * @param callback - The callback to call when the widget is opened
 */
export const onOpen: (args_0: () => void) => void = createCommand('onOpen');

/**
 * Subscribe to the widget close event
 * @param callback - The callback to call when the widget is closed
 */
export const onClose: (args_0: () => void) => void = createCommand('onClose');

/**
 * Get the current conversation
 * @param callback - The callback to call when the conversation is retrieved
 */
export const getCurrentConversation: (args_0: (conversation: Conversation | null) => void) => void =
	createCommand('getCurrentConversation');
